import moment, { Moment, MomentInput } from 'moment'
import React, { useMemo, useRef, useLayoutEffect } from 'react'
import { FlatList, StyleProp, Text, ViewStyle } from 'react-native'
import {
  DayCircleContainer,
  DayContainer,
  DaysContainer,
  MonthLabel,
  ROW_HEIGHT,
  StreakContainer,
  TodayMark,
  WeekRowContainer,
  WeeksList,
} from './style'
import { getWeeks } from './utils'
import Typography from 'APP/Converse/Typography'
import I18n from 'APP/I18n'

interface WeekProps {
  days: Moment[]
  isFirst: boolean
  isLast: boolean
  today: Moment
  activeDates: Record<string, boolean>
}

const Week: React.FC<WeekProps> = ({ days, isFirst, isLast, today, activeDates }) => {
  let activeDays = 0
  return (
    <WeekRowContainer isFirst={isFirst} isLast={isLast}>
      <DaysContainer>
        {days.map((day) => {
          const isHidden =
            (isFirst && !day.isSame(days[6], 'M')) || (isLast && !day.isSame(days[0], 'M'))
          const isToday = today.isSame(day, 'day')
          const isInactive = today.isBefore(day, 'day')
          const isActive = !isInactive && activeDates[day.format(I18n.t('DateFormat'))]
          const isStartOfTheMonth = day.isSame(day.clone().startOf('month'), 'D')
          if (isActive) activeDays++
          return (
            <DayContainer key={day.toString()} isHidden={isHidden}>
              {isStartOfTheMonth && (
                <MonthLabel
                  variant="subheaderBold"
                  color="text"
                  testID={`month-${day.format('MM-YYYY')}`}
                >
                  {day.format('MMM')}
                </MonthLabel>
              )}
              <DayCircleContainer isActive={isActive}>
                <Typography
                  variant="subheader"
                  color={isActive ? 'darkText' : isInactive ? 'disabled' : 'text'}
                >
                  {day.format('D')}
                </Typography>
              </DayCircleContainer>
              {isToday && <TodayMark testID={'todayMark'} />}
            </DayContainer>
          )
        })}
      </DaysContainer>
      <StreakContainer>
        {activeDays > 2 && <Text>🔥</Text>}
        <Typography variant="caption" color="text">
          {activeDays > 2 ? `3+` : activeDays}
        </Typography>
      </StreakContainer>
    </WeekRowContainer>
  )
}

interface CalendarListProps {
  startDay: MomentInput
  endDay?: MomentInput
  activeDates?: Record<string, boolean>
  style?: StyleProp<ViewStyle>
}

export const CalendarList: React.FC<CalendarListProps> = ({
  startDay,
  endDay,
  activeDates = {},
  style: styleProp,
}) => {
  const weeks = useMemo(() => getWeeks(startDay, endDay), [startDay, endDay])
  const today = moment()
  const flatListRef = useRef<FlatList<Moment[]>>(null)

  useLayoutEffect(() => {
    if (flatListRef.current && weeks && weeks.length > 0) {
      flatListRef.current.scrollToEnd()
    }
  }, [])

  return (
    <WeeksList
      ref={flatListRef}
      data={weeks}
      keyExtractor={(days) => days[0].toString()}
      style={styleProp}
      getItemLayout={(_, index) => ({ length: ROW_HEIGHT, offset: ROW_HEIGHT * index, index })}
      renderItem={({ item, index }) => (
        <Week
          isFirst={index === 0}
          isLast={index + 1 === weeks.length}
          days={item}
          today={today}
          activeDates={activeDates}
        />
      )}
    />
  )
}
